import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { INZorroOutput } from '../../../core/models/common';
import { CustomTableColumn } from '../../../core/models/custom-table-fields';
import { ToasterService } from '../../../core/services/toaster-service';
import { AddColumnModalComponent } from '../../modal-templates/add-column-modal/add-column-modal.component';
import { ZorroTableComponent } from '../custom-table/zorro-table.component';
import { MessageEnum } from '../../../core/utils/message.enum';

@Component({
  selector: 'app-editable-table',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, ZorroTableComponent],
  templateUrl: './editable-table.component.html',
  styleUrl: './editable-table.component.scss'
})
export class EditableTableComponent implements OnInit {
  /**
   * Input property for table data containing columns and rows.
   */
  @Input() tableData: { columns: CustomTableColumn[], data: any[] } = {
    columns: [
      {
        key: 'a',
        display_name: 'Column A',
        cellStyles: { 'font-size': '12px' },
        headerStyles: { 'font-weight': '600' },
        _editConfig: {
          type: 'text'
        }
      },
      {
        key: 'b',
        display_name: 'Column B',
        cellStyles: { 'font-size': '12px' },
        headerStyles: { 'font-weight': '600' },
        _editConfig: {
          type: 'text'
        }
      },
      {
        key: 'c',
        display_name: 'Column C',
        cellStyles: { 'font-size': '12px' },
        headerStyles: { 'font-weight': '600' },
        _editConfig: {
          type: 'text'
        }
      },
      {
        key: 'd',
        display_name: 'Column D',
        cellStyles: { 'font-size': '12px' },
        headerStyles: { 'font-weight': '600' },
        _editConfig: {
          type: 'text'
        }
      }
    ],
    data: [
      {
        a: 'sdas',
        b: 'nknxc',
        c: 'ccdoj',
        d: 'kjbaskdjjksa'
      },
    ]
  };

  /**
   * Input property for table metadata and options.
   */
  @Input() meta: any = {};

  /**
   * Input property for table display options.
   */
  @Input() options: any = {};

  /**
   * Input property for autocomplete options for input fields.
   */
  @Input() autocompleteOptions: any = {};

  /**
   * Input property for loading state.
   */
  @Input() isLoading: boolean = false;

  /**
   * Input property for table ID (used for pagination state management).
   */
  @Input() tableId: string = '';

  /**
   * Output event emitted when delete action is triggered.
   */
  @Output() onDeleteRows = new EventEmitter<any[]>();

  /**
   * Output event emitted when insert action is triggered.
   */
  @Output() onInsertRow = new EventEmitter<{ type: 'before' | 'after' | 'new', selectedRows?: any[] }>();

  /**
   * Output event emitted when save action is triggered.
   */
  @Output() onSaveData = new EventEmitter<any>();

  /**
   * Output event emitted when row or column selection changes.
   */
  @Output() onRowSelect = new EventEmitter<INZorroOutput>();

  /**
   * Output event emitted when table actions are triggered.
   */
  @Output() handleActions = new EventEmitter<any>();

  /**
   * Output event emitted when pagination changes.
   */
  @Output() pageChange = new EventEmitter<{ page: number, size: number }>();

  /**
   * Output event emitted when filter actions are triggered.
   */
  @Output() filterActions = new EventEmitter<any>();

  /**
   * Track selected rows
   */
  selectedRows: any[] = [];

  /**
   * Track if there are unsaved changes
   */
  hasChanges: boolean = false;

  private readonly modalService = inject(NgbModal);
  isSelectAll = false;

  @ViewChild(ZorroTableComponent) zorroTableComponent!: ZorroTableComponent;

  constructor(private readonly toaster: ToasterService) {}

  ngOnChanges(changes: SimpleChanges) {
  if (changes['tableData']?.currentValue) {
    this.setInitialProps();
  }
}
  ngOnInit() {
    // this.initializeTable();
    // this.setInitialProps();
  }

  ngAfterViewInit() {
    this.setInitialProps();
  }

  hasColumn(key: string){
    return this.tableData?.columns?.some((col: any) => col.key === key);
  }

  setInitialProps() {
    if (!this.hasColumn('edit')) {
      this.tableData = {
        ...this.tableData,
        columns: [
          ...this.tableData.columns,
          {
            key: "edit",
            display_name: "",
            cellStyles: {
              "font-size": "12px",
              position: "sticky",
              right: "0",
              "z-index": "2",
              "background-color": "#fff"
            },
            headerStyles: {
              background: "#7593F2",
              "font-weight": "600",
              width: "3%",
              color: "#fff",
              position: "sticky",
              right: "0",
              "z-index": "2",
              "background-color": "#fff"
            },
            _config: {
              type: "svg-editor",
              value: "../../../../assets/images/virtusa-icons/edit-light.svg",
              isClickable: "true",
              actionType: "edit"
            },
            _editConfig: {
              type: "svg-editor",
              actionType: "add",
              value: "../../../../assets/images/virtusa-icons/tick.svg",
              isClickable: "true"
            }
          }
        ]
      };
    }

    if (!this.hasColumn('rowSelect')) {
      const rowSelectColumn = {
        key: "rowSelect",
        display_name: "",
        headerStyles: {
          width: "5%"
        },
        _config: {
          type: "checkbox"
        }
    };

    this.tableData = {
      ...this.tableData,
      columns: [rowSelectColumn, ...this.tableData.columns]
    };
  }

    if (this.zorroTableComponent) {
      this.zorroTableComponent.updateOptions({ enableColumnSelection: true });
    }
  }

  /**
   * Handle delete action
   * Deletes either selected rows or selected columns
   */
  onDelete() {
    // Check if any columns are selected
    const selectedColumns = this.tableData.columns.filter((col: any) => col.select === true);
    
    if (selectedColumns.length > 0) {
      // Delete selected columns without confirmation
      // Get the keys of columns to delete
      const colKeysToDelete = selectedColumns.map((col: any) => col.key);
      
      // Remove columns from tableData.columns
      this.tableData.columns = this.tableData.columns.filter((col: any) => 
        !colKeysToDelete.includes(col.key)
      );
      
      // Remove column data from all rows
      this.tableData.data.forEach((row: any) => {
        colKeysToDelete.forEach((colKey: string) => {
          delete row[colKey];
        });
      });
      
      // Clear selected columns
      this.tableData.columns.forEach((col: any) => {
        col.select = false;
      });
      
      // Refresh table display
      this.zorroTableComponent.reOrderExpandableRows();
    } else if (this.selectedRows.length > 0) {
      // Delete selected rows without confirmation
      // Remove selected rows from tableData.data
      this.tableData.data = this.tableData.data.filter((row: any) => 
        !this.selectedRows.some((selectedRow: any) => selectedRow.id === row.id)
      );
      
      // Clear selected rows
      this.selectedRows = [];

      // Refresh table display
      this.zorroTableComponent.reOrderExpandableRows();
    }
  }

  /**
   * Handle save action
   */
  onSave() {
    if (this.tableData?.data) {
      this.onSaveData.emit(this.tableData);
      this.hasChanges = false;
    }
  }

  getSelectionCount(): any {
    let colSelect = this.tableData.columns.filter(col => col.select).length;
    if(colSelect) {
      return `Columns (${colSelect})`;
    } else if(this.selectedRows.length) {
      return `Rows (${this.selectedRows.length})`;
    } else {
      return '';
    }
  }

  /**
   * Handle row or column selection from the ZorroTableComponent.
   * @param event Selection event from the table.
   */
  onTableRowSelect(event: INZorroOutput) {
    if (event.type === 'rowSelect' && Array.isArray(event.data)) {
      this.selectedRows = event.data;
    }
    this.onRowSelect.emit(event);

    if(this.selectedRows.length !== this.tableData.data.length) {
      this.isSelectAll = false;
    } else {
      this.isSelectAll = true;
    }
  }

  /**
   * Handle table actions from the ZorroTableComponent.
   * @param event Action event from the table.
   */
  onTableAction(event: any) {
    if (event.actionType === "add") {
      let rowId = event.data?.row?.id;
      if (this.isRowEmpty(event?.data?.row)) {
        this.toaster.pop({
          text: MessageEnum.EMPTY_ROW,
          type: "error",
        });
      } else {
        this.tableData.data[this.tableData.data.findIndex(tc => tc.id === rowId)].isRowEditable = event.actionType === "edit";
        this.tableData = { ...this.tableData };
        this.zorroTableComponent.initializeTable();
      }
    }
    if (event.actionType === "edit") {
      let rowId = event.data?.row?.id;
      this.tableData.data[this.tableData.data.findIndex(tc => tc.id === rowId)].isRowEditable = event.actionType === "edit";
      this.tableData = { ...this.tableData };
      this.zorroTableComponent.initializeTable();
    }
    // this.handleActions.emit(event);
  }

  isRowEmpty(row: any): boolean {
    const ignoreKeys = ['id', 'edit', 'isRowEditable', 'rowSelect', 'expand', 'level'];
    return Object.keys(row)
      .filter(key => !ignoreKeys.includes(key))
      .every(key => {
        const value = row[key];
        return value === '' || value === null || value === undefined;
      });
  }

  /**
   * Handle Select All checkbox click
   * Selects all rows and deselects any columns that were selected
   */
  onSelectAllRows(event: any) {
    const isChecked = event.target.checked;
    
    if (isChecked) {
      // Deselect any columns that were selected
      this.tableData.columns.forEach((col: any) => {
        col.select = false;
      });
      
      // Select all rows
      this.tableData.data.forEach((row: any) => {
        row.select = true;
      });
      
      this.selectedRows = [...this.tableData.data];
    } else {
      // Deselect all rows
      this.tableData.data.forEach((row: any) => {
        row.select = false;
      });
      
      this.selectedRows = [];
    }
    
    // Notify the table to refresh and emit the selection
    this.zorroTableComponent.reOrderExpandableRows();
    this.onRowSelect.emit({
      type: "rowSelect",
      select_type: isChecked ? "all" : "",
      data: this.selectedRows,
    } as any);
  }

  insert(type: 'column' | 'row') {
      if (type === 'row') {
        let obj: any = {
          isRowEditable: true
        };
        this.tableData.columns.forEach((col: any) => {
          obj[col.key] = '';
        });
        this.tableData.data.push(obj);
        this.tableData.data = [...this.tableData.data];
      } else {
        let modalRef = this.modalService.open(AddColumnModalComponent, {
          windowClass:"add-cloumn-window",
          centered: true,
          size: "sm"
        });
        modalRef.result.then((result: any) => {
          if (result) {
            // Insert before the last element (action column)
            this.tableData.columns.splice(this.tableData.columns.length - 1, 0, { key: result, display_name: result, _editConfig: { type: "input" }, headerStyles: { 'min-width': '200px !important' }});
            this.tableData.data.forEach((row: any) => {
              row[result] = '';
            });
            this.tableData.data = [...this.tableData.data];
          }
        });
      }
      this.zorroTableComponent.initializeTable();
    }
    
}
